import { use, useCallback, useEffect, useRef, useState } from "react";
import { ed, EdState, loadNewFile } from "../ed";
import { playTypewriterDingSound, playTypewriterKeySound, playTypewriterNoise, setUpAudio, stopTypewriterNoise } from "../utils/audioUtils";
import { delay, getScreenCenterCoords, isProbablyText, readUploadedFile, visualLength, wrap } from "../utils/utils";
import { Controls } from "./Controls";
import { addNewLine, DesktopState, preprocessInput, typeChar } from "./desktopState";
import "../index.css";
import { Pinboard } from "./Pinboard";
import { Teletype } from "./Teletype";

const CODE_PRELUDE = `
  self.console.log = (...args) => {
    const output = args.map(arg => {
      if (typeof arg === 'object') {
        return JSON.stringify(arg, null, 2);
      }
      return arg;
    }).join(' ');
    self.postMessage({ type: "log", payload: output });
  };

  self.write = (filename, content) => {
    files[filename] = content;
    self.postMessage({ type: "write", filename, content });
  };

  self.read = (filename) => {
    if (files[filename] === undefined) {
      return "";
    }
    return files[filename];
  };
`;
const CODE_POSTLUDE = `
;
self.postMessage({ type: "done" });
`;

async function runJavascript(userJavascript: string, argv: string[], files: Record<string, string>): Promise<[string, Record<string, string>]> {
  return new Promise<[string, Record<string, string>]>((resolve, reject) => {
    let returnFiles = {...files};
    let callingCode = `
let files = ${JSON.stringify(files)};
let argv = ${JSON.stringify(argv)};
`;
    let codeBlob = new Blob([CODE_PRELUDE, callingCode, userJavascript, CODE_POSTLUDE], { type: "text/javascript" });
    let codeUrl = URL.createObjectURL(codeBlob);
    let worker = new Worker(codeUrl);
    let aggLogs: string[] = [];
    worker.onmessage = (event) => {
      if (event.data.type === "log") {
        aggLogs.push(event.data.payload);
        return;
      }
      if (event.data.type === "done") {
        worker.terminate();
        URL.revokeObjectURL(codeUrl);
        resolve([aggLogs.join("\n"), returnFiles]);
        return;
      }
      if (event.data.type === "write") {
        returnFiles[event.data.filename] = event.data.content;
        return;
      }
    };
    worker.onerror = (error) => {
      worker.terminate();
      URL.revokeObjectURL(codeUrl);
      resolve(["Error: " + error.message, returnFiles]);
    };
    setTimeout(() => {
      worker.terminate();
      URL.revokeObjectURL(codeUrl);
      resolve(["Error: Timeout", returnFiles]);
    }, 5 * 1000);
  });
}

export function Desktop({ scenario, savedDesktopState, savedEdState, onSave }: { scenario: {id: string, initialDesktopState: DesktopState, initialEdState: EdState }, savedDesktopState: DesktopState, savedEdState: EdState, onSave: (desktopState: DesktopState, edState: EdState) => void }) {
  const [desktopState, setDesktopState] = useState(savedDesktopState);
  const edState = useRef<EdState>(savedEdState);
  const shiftHeld = useRef(false);
  const isTyping = useRef(false);
  const onSaveDebounced = useCallback(debounce(onSave, 500), [onSave]);
  const volumeRef = useRef<"SILENT" | "NOISY">(desktopState.volume);
  useEffect(() => {
    volumeRef.current = desktopState.volume;
    if (desktopState.volume === "NOISY") {
      // playTypewriterNoise();
    } else {
      // stopTypewriterNoise();
    }
  }, [desktopState.volume]);

  useEffect(() => {
    setUpAudio();

    async function runEdCommand(inputLine: string) {
      await delay(1000/20);
        setDesktopState(s => addNewLine(s));
        await delay(1000/20);
        let [newState, outputLines] = ed(edState.current, inputLine);
        for (let outputLine of outputLines) {
          if (outputLine == "of papers on your desk.") {
            setDesktopState(s => ({
              ...s,
              pinboard: {
                ...s.pinboard,
                papers: [
                  ...s.pinboard.papers,
                  { id: Math.random().toString(), type: "image", pos: getScreenCenterCoords(), imageUrl: "./public/ken-marked-up-listing.jpg" }
                ]
              }
            }));
          }
          while (outputLine.length > 0) {
            playTypewriterKeySound(volumeRef.current, outputLine[0]);
            await delay(1000/20);
            let chunk = shiftHeld.current ? outputLine.slice(0, desktopState.turboSpeed) : outputLine[0];
            outputLine = outputLine.slice(chunk.length);
            setDesktopState(s => typeChar(s, chunk));
            await delay(1000/20);
          }
          playTypewriterKeySound(volumeRef.current, "\n");
          await delay(1000/20);
          setDesktopState(s => addNewLine(s));
          await delay(1000/20);
        }
        edState.current = newState;
        onSaveDebounced(desktopState, edState.current);
    }

    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.shiftKey) {
        shiftHeld.current = true;
      }
      if (isTyping.current) return;
      let char = "";
      if (event.ctrlKey && event.key === "i") {
        event.preventDefault();
        char = "\x0E";
      } else if (event.ctrlKey && event.key === "o") {
        event.preventDefault();
        char = "\x0F";
      } else if (event.ctrlKey && event.key === "g") {
        event.preventDefault();
        char = "\x07";
      } else if (event.ctrlKey || event.metaKey) {
        return;
      } else if (event.altKey && (event.code == "KeyV" || event.code == "KeyH")) {
        event.preventDefault();
        setDesktopState({ ...desktopState, tool: "HAND" })
        return;
      } else if (event.altKey && (event.code == "KeyS" || event.code == "KeyC" || event.code == "KeyX")) {
        event.preventDefault();
        setDesktopState({ ...desktopState, tool: "SCISSORS" })
        return;
      } else if (event.altKey && (event.code == "KeyB" || event.code == "KeyD")) {
        event.preventDefault();
        setDesktopState({ ...desktopState, tool: "BIN" })
        return;
      } else if (event.key.length === 1) {
        event.preventDefault();
        char = event.key;
      } else if (event.key === "Tab") {
        event.preventDefault();
        char = "\t";
      } else if (event.key === "Backspace" && desktopState.teletype.line.length > 0) {
        event.preventDefault();
        char = "\b";
      } else if (event.key === "Enter") {
        event.preventDefault();
        let inputLine = preprocessInput(desktopState.teletype.line);
        isTyping.current = true;
        playTypewriterKeySound(volumeRef.current, "\n");
        if (inputLine.startsWith("!") && edState.current.mode === "PROMPT") {
          setDesktopState(s => addNewLine(s));
          await delay(1000/20);
          let argv = inputLine.slice(1).trim().split(/\ +/);
          let filename = argv[0];
          let file = edState.current.files[filename];
          if (file === undefined) {
            await delay(1000/20);
            setDesktopState(s => typeChar(s, "?"));
            await delay(1000/20);
            setDesktopState(s => addNewLine(s));
            await delay(1000/20);
            isTyping.current = false;
            return;
          }
          let [output, newFiles] = await runJavascript(file, argv, edState.current.files);
          let outputLines = wrap(output);
          for (let outputLine of outputLines) {
            while (outputLine.length > 0) {
              playTypewriterKeySound(volumeRef.current, outputLine[0]);
              await delay(1000/20);
              let chunk = shiftHeld.current ? outputLine.slice(0, desktopState.turboSpeed) : outputLine[0];
              outputLine = outputLine.slice(chunk.length);
              setDesktopState(s => typeChar(s, chunk));
              await delay(1000/20);
            }
            playTypewriterKeySound(volumeRef.current, "\n");
            await delay(1000/20);
            setDesktopState(s => addNewLine(s));
            await delay(1000/20);
          }
          edState.current = { ...edState.current, files: { ...newFiles } };
          onSaveDebounced(desktopState, edState.current);
        } else {
          await runEdCommand(inputLine);
        }

        isTyping.current = false;
        return;
      } else {
        return;
      }

      isTyping.current = true;
      event.preventDefault();
      if (visualLength(desktopState.teletype.line + char) > 69) {
        playTypewriterDingSound(volumeRef.current);
      } else {
        playTypewriterKeySound(volumeRef.current, char);
      }
      await delay(1000/20);
      setDesktopState(s => typeChar(s, char));
      await delay(1000/20);
      isTyping.current = false;
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        shiftHeld.current = false;
      }
    }

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      event.dataTransfer!.dropEffect = "copy";
    }
    const handleDrop = async (event: DragEvent) => {
      event.preventDefault();
      isTyping.current = true;
      const files = event.dataTransfer!.files;
      if (files.length > 0) {
        const file = files[0];
        const { name, content } = await readUploadedFile(file);
        if (isProbablyText(content)) {
          edState.current = loadNewFile(edState.current, name, content);
          onSaveDebounced(desktopState, edState.current);
          if (desktopState.teletype.line.length > 0) {
            await delay(1000/20);
            setDesktopState(s => typeChar(s, "@"));
            await delay(1000/20);
          }
          let inputLineBuffer = `e ${name}`;
          let inputLine = inputLineBuffer;
          while (inputLineBuffer.length > 0) {
            playTypewriterKeySound(volumeRef.current, inputLineBuffer[0]);
            await delay(1000/20);
            let chunk = shiftHeld.current ? inputLineBuffer.slice(0, desktopState.turboSpeed) : inputLineBuffer[0];
            inputLineBuffer = inputLineBuffer.slice(chunk.length);
            setDesktopState(s => typeChar(s, chunk));
            await delay(1000/20);
          }
          playTypewriterKeySound(volumeRef.current, "\n");
          await delay(1000/20);
          setDesktopState(s => addNewLine(s));
          await delay(1000/20);
          let [newState, outputLines] = ed(edState.current, inputLine);
          for (let outputLine of outputLines) {
            while (outputLine.length > 0) {
              playTypewriterKeySound(volumeRef.current, outputLine[0]);
              await delay(1000/20);
              let chunk = shiftHeld.current ? outputLine.slice(0, desktopState.turboSpeed) : outputLine[0];
              outputLine = outputLine.slice(chunk.length);
              setDesktopState(s => typeChar(s, chunk));
              await delay(1000/20);
            }
            playTypewriterKeySound(volumeRef.current, "\n");
            await delay(1000/20);
            setDesktopState(s => addNewLine(s));
            await delay(1000/20);
          }
          edState.current = newState;
          onSaveDebounced(desktopState, edState.current);
          isTyping.current = false;
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    }
  }, [desktopState]);

  useEffect(() => {
    onSaveDebounced(desktopState, edState.current);
  }, [desktopState, edState.current]);

  const handleReset = useCallback(() => {
    setDesktopState(scenario.initialDesktopState);
    edState.current = scenario.initialEdState;
    onSaveDebounced(desktopState, edState.current);
    onSave(scenario.initialDesktopState, scenario.initialEdState);
    if (isTyping.current) {
      window.location.reload();
    }
  }, [scenario, onSave]);

  return (
    <main>
      <Teletype desktopState={desktopState} setDesktopState={setDesktopState} />
      <Pinboard desktopState={desktopState} setDesktopState={setDesktopState} />
      <Controls desktopState={desktopState} setDesktopState={setDesktopState} onReset={handleReset} />
    </main>
  );
}

function debounce(func: Function, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return function(...args: any[]) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}
